import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import type { ApplicationChecklist } from '../../data/types';
import { date, DEMO_TODAY, money } from '../../lib/format';
import { HOLDER_TYPE_LABELS, KYC_TYPE_LABELS } from '../../lib/labels';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { ConfirmDialog, useToast } from '../../ui/feedback';
import { KeyValueGrid, PageHeader, SectionCard, StatusChip } from '../../ui/primitives';
import { applicationReadiness } from '../../domain/kyc/readiness';
import { wholesaleModeFor } from '../../domain/kyc/wholesale';
import { applicationPayment } from '../../domain/raise/raiseCalc';
import ApplicationIssuePanel from './ApplicationIssuePanel';
import ApplicationPaymentPanel from './ApplicationPaymentPanel';
import { CHECKLIST_LABELS } from './ChecklistDots';
import { entityPath, sampleHolderPath } from '../../entities/entityPaths';

const ICONS = {
  DONE: <CheckCircleIcon fontSize="small" sx={{ color: '#16A34A' }} />,
  PENDING: <RadioButtonUncheckedIcon fontSize="small" sx={{ color: '#F59E0B' }} />,
  ISSUE: <ErrorIcon fontSize="small" sx={{ color: '#DC2626' }} />,
  NA: <RadioButtonUncheckedIcon fontSize="small" color="disabled" />,
};

export default function ApplicationDetailPage() {
  const { applicationId } = useParams();
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const [rejecting, setRejecting] = useState(false);
  const application = scope.applications.find((item) => item.id === applicationId);
  if (!application) return <Navigate to="/manager/applications" replace />;

  const fund = scope.fundById.get(application.fundId);
  const unitClass = scope.classById.get(application.classId);
  const kycCases = scope.kycCases.filter((item) => item.applicationId === application.id);
  const bankLine = scope.bankLines.find(
    (line) => line.suggestedApplicationId === application.id && line.status !== 'MATCHED',
  );
  const final = ['ISSUED', 'REJECTED', 'WITHDRAWN'].includes(application.status);
  const fundsDone = application.checklist.funds === 'DONE';
  const closeOffer = scope.offers.find(
    (offer) => offer.classId === application.classId && offer.issueAtClose,
  );
  const paid = applicationPayment(application, unitClass).status === 'PAID';
  const holder = scope.holderByEntityId.get(application.investmentEntityId);
  const readiness = applicationReadiness(
    application,
    holder,
    scope.kycCases,
    DEMO_TODAY,
    undefined,
    wholesaleModeFor(state, application.fundId),
  );

  const primaryAction = () => {
    switch (application.status) {
      case 'INVITED':
      case 'IN_PROGRESS':
        return (
          <Button
            variant="contained"
            onClick={() => {
              dispatch({ type: 'APPLICATION_SEND_REMINDER', id: application.id });
              toast('Reminder sent by email and push notification');
            }}
          >
            Send reminder
          </Button>
        );
      case 'SUBMITTED':
        return (
          <Button
            variant="contained"
            onClick={() => {
              dispatch({
                type: 'APPLICATION_SET_STATUS',
                id: application.id,
                status: 'AWAITING_FUNDS',
                note: 'Checklist reviewed; identity verified electronically',
              });
              toast('Checklist accepted. Waiting for application monies');
            }}
          >
            Accept checklist
          </Button>
        );
      case 'KYC_REVIEW':
        return (
          <Button component={RouterLink} to="/manager/kyc" variant="contained">
            Resolve in KYC queue
          </Button>
        );
      case 'AWAITING_FUNDS':
        if (closeOffer && !application.issueRunId) {
          return paid ? (
            <Button
              component={RouterLink}
              to={`/manager/offers/${closeOffer.id}/close`}
              variant="contained"
            >
              Issue at financial close
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                dispatch({ type: 'APPLICATION_PAYMENT_REMINDER', id: application.id });
                toast('Payment reminder sent');
              }}
            >
              Send payment reminder
            </Button>
          );
        }
        return fundsDone ? (
          <Button
            variant="contained"
            onClick={() => {
              dispatch({ type: 'APPLICATION_REQUEST_ISSUE', id: application.id });
              toast('Unit issue sent to Approvals');
            }}
          >
            Submit unit issue for approval
          </Button>
        ) : (
          <Button component={RouterLink} to="/manager/payments" variant="contained">
            Match application monies
          </Button>
        );
      case 'PENDING_APPROVAL':
        return (
          <Button component={RouterLink} to="/manager/approvals" variant="contained">
            Open approvals
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={`${application.reference} · created ${date(application.createdAt)}`}
        title={application.applicantName}
        subtitle={<StatusChip status={application.status} />}
        actions={
          <>
            {!final && (
              <Button color="error" onClick={() => setRejecting(true)}>
                Reject
              </Button>
            )}
            {primaryAction()}
          </>
        }
      />
      {!final && readiness.issues.length > 0 && (
        <Alert severity={readiness.ready ? 'info' : 'warning'} sx={{ mb: 2 }}>
          <strong>
            {readiness.ready ? 'Investor checks: to watch' : 'Investor checks to resolve'}
          </strong>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {readiness.issues.map((issue) => (
              <li key={issue.text}>{issue.text}</li>
            ))}
          </Box>
        </Alert>
      )}
      {bankLine && !fundsDone && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button component={RouterLink} to="/manager/payments" size="small">
              Review match
            </Button>
          }
        >
          A bank receipt of {money(bankLine.amount)} on {date(bankLine.date)} looks like this
          application&apos;s monies.
        </Alert>
      )}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '2fr 3fr' } }}>
        <SectionCard
          title="Checklist"
          subtitle="Completed by the investor in the app, verified automatically where possible"
          noPadding
        >
          <List dense disablePadding>
            {(Object.keys(CHECKLIST_LABELS) as (keyof ApplicationChecklist)[]).map((key) => (
              <ListItem key={key} sx={{ px: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  {ICONS[application.checklist[key]]}
                </ListItemIcon>
                <ListItemText
                  primary={CHECKLIST_LABELS[key]}
                  secondary={
                    application.checklist[key] === 'ISSUE'
                      ? 'Needs review'
                      : application.checklist[key] === 'PENDING'
                        ? 'Waiting on investor'
                        : 'Complete'
                  }
                />
              </ListItem>
            ))}
          </List>
        </SectionCard>
        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
          <SectionCard title="Application">
            <KeyValueGrid
              items={[
                { label: 'Fund', value: `${fund?.name} (${fund?.code})` },
                { label: 'Class', value: unitClass?.name },
                { label: 'Amount', value: money(application.amount) },
                {
                  label: 'Units on issue (est.)',
                  value: unitClass
                    ? Math.round(application.amount / unitClass.unitPrice).toLocaleString('en-AU')
                    : '—',
                },
                { label: 'Applicant type', value: HOLDER_TYPE_LABELS[application.applicantType] },
                {
                  label: 'Contact',
                  value: [application.contactName, application.email].filter(Boolean).join(' · '),
                },
                {
                  label: 'Investment entity',
                  value: (
                    <RouterLink to={entityPath(application.investmentEntityId)}>
                      View entity
                    </RouterLink>
                  ),
                },
                {
                  label: 'Allocation',
                  value: application.allocationId ? 'Based on the entity allocation' : 'None',
                },
                {
                  label: 'Registry record',
                  value: holder ? (
                    <RouterLink to={sampleHolderPath(holder.id)}>{holder.accountNumber}</RouterLink>
                  ) : (
                    'Created when units are issued'
                  ),
                },
                { label: 'Submitted', value: date(application.submittedAt) },
                { label: 'Funds received', value: date(application.fundsReceivedAt) },
              ]}
            />
          </SectionCard>
          <ApplicationIssuePanel application={application} />
          {application.submittedAt && (
            <ApplicationPaymentPanel application={application} unitClass={unitClass} />
          )}
          {kycCases.length > 0 && (
            <SectionCard title="Open KYC cases">
              {kycCases.map((item) => (
                <Box key={item.id} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {KYC_TYPE_LABELS[item.type]} <StatusChip status={item.status} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.detail}
                  </Typography>
                </Box>
              ))}
            </SectionCard>
          )}
          <SectionCard title="Notes">
            {application.notes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No notes yet.
              </Typography>
            ) : (
              application.notes.map((note) => (
                <Typography key={note} variant="body2" sx={{ mb: 0.75 }}>
                  • {note}
                </Typography>
              ))
            )}
          </SectionCard>
        </Box>
      </Box>
      <ConfirmDialog
        open={rejecting}
        title="Reject application"
        body="The investor is notified in the app. Any monies received are returned to the source account."
        confirmLabel="Reject application"
        requireComment
        commentLabel="Reason"
        danger
        onClose={() => setRejecting(false)}
        onConfirm={(reason) => {
          dispatch({ type: 'APPLICATION_REJECT', id: application.id, reason });
          setRejecting(false);
          toast('Application rejected', 'warning');
        }}
      />
    </>
  );
}
